import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Heading,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useUpsertNexIntegration } from '../../../hooks/useSaveIntegration';
import PartBayButton from '../../PartBayButton';
import ConnectionBadge from './ConnectionBadge';
import { CONNECTIONS } from './catalog';

export interface NexGuidModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentGuid: string | null;
}

const NexGuidModal = ({ isOpen, onClose, currentGuid }: NexGuidModalProps) => {
    const toast = useToast();
    const [guid, setGuid] = useState('');

    // Re-seed the input from the saved value each time the modal opens.
    useEffect(() => {
        if (isOpen) setGuid(currentGuid ?? '');
    }, [isOpen, currentGuid]);

    const upsertNexMutation = useUpsertNexIntegration({
        onError: (err) => {
            toast({
                title: 'Failed to save Nexsyis connection',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        },
    });

    const isSaving = upsertNexMutation.isPending;
    const trimmedGuid = guid.trim();
    const isValid = trimmedGuid.length > 0 && trimmedGuid !== (currentGuid ?? '');

    const handleClose = () => {
        if (isSaving) return;
        onClose();
    };

    const handleSubmit = async () => {
        if (!isValid) return;
        await upsertNexMutation.mutateAsync({ nexApiCustomerGuid: trimmedGuid });
        toast({
            title: 'Nexsyis connection saved',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader pb={3}>
                    <HStack spacing={3} align="center">
                        <ConnectionBadge id="nex" size={40} />
                        <Box>
                            <Heading size="md" color="gray.800">
                                {CONNECTIONS.nex.label}
                            </Heading>
                            <Text fontSize="xs" color="gray.500" fontWeight="normal">
                                Organization-level connection
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton isDisabled={isSaving} />
                <ModalBody pb={2}>
                    <VStack spacing={4} align="stretch">
                        <Text fontSize="xs" color="gray.500">
                            The customer GUID identifies your organization to the Nexsyis API and is
                            provided by Nexsyis.
                        </Text>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Customer GUID</FormLabel>
                            <Input
                                size="sm"
                                value={guid}
                                onChange={(e) => setGuid(e.target.value)}
                                placeholder="e.g. 1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d"
                                fontFamily="mono"
                                autoComplete="off"
                            />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outlineGray"
                        size="action"
                        onClick={handleClose}
                        isDisabled={isSaving}
                        mr={2}
                    >
                        Cancel
                    </Button>
                    <PartBayButton
                        text="Save"
                        variant="solid"
                        colorScheme="partbay-blue"
                        size="action"
                        onClick={() => void handleSubmit()}
                        isDisabled={!isValid}
                        isLoading={isSaving}
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NexGuidModal;
